import { useState } from 'react';
import { motion } from 'framer-motion';
import { PageHero, SectionHeader } from '@/components/common/Sections';
import { CTABanner } from '@/components/common/Cards';
import { IMAGES } from '@/lib/constants';
import { Handshake, ChartLineUp, Globe, Users } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { submitPartnerApplication } from '@/lib/api';
import { toast } from 'sonner';

export default function PartnerWithUsPage() {
  const [formData, setFormData] = useState({ company_name: '', contact_person: '', email: '', phone: '', partnership_type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitPartnerApplication(formData);
      toast.success('Partnership application submitted! Our team will contact you soon.');
      setFormData({ company_name: '', contact_person: '', email: '', phone: '', partnership_type: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const partnershipTypes = [
    { icon: Handshake, title: "Travel Agents", description: "Join our network of trusted travel partners" },
    { icon: ChartLineUp, title: "Corporate Partners", description: "Partner for corporate travel solutions" },
    { icon: Globe, title: "Tourism Boards", description: "Collaborate on destination marketing" },
    { icon: Users, title: "Technology Partners", description: "Integrate with our platforms" },
  ];

  return (
    <div className="page-transition">
      <PageHero title="Partner With Us" subtitle="Grow your business with Osaka Group" backgroundImage={IMAGES.corporateTravel} />

      <section className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader overline="Partnership Opportunities" title="Why Partner With Osaka?" description="Join hands with Kerala's most trusted travel management company." />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnershipTypes.map((type, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="text-center p-6 bg-[#F5F5F5] card-hover">
                <type.icon size={40} className="text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-2">{type.title}</h3>
                <p className="text-[#4A5568] text-sm">{type.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#0A1F3D] relative grain-overlay">
        <div className="container-custom relative z-10">
          <SectionHeader overline="Benefits" title="What You Get" light />
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {["Access to 500+ airline partners", "Competitive commission structure", "Dedicated support team", "Training & certification programs", "Marketing collaterals", "Co-branding opportunities"].map((benefit, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#D4AF37]" />
                <span className="text-white/80">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" data-testid="partner-application" className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto bg-white p-8 md:p-12">
            <SectionHeader overline="Get Started" title="Apply for Partnership" />
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Company Name *</label>
                  <Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} data-testid="partner-company-input" required className="rounded-none border-slate-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Contact Person *</label>
                  <Input value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} data-testid="partner-contact-input" required className="rounded-none border-slate-300" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Email *</label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="partner-email-input" required className="rounded-none border-slate-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Phone *</label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="partner-phone-input" required className="rounded-none border-slate-300" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Partnership Type *</label>
                <Select value={formData.partnership_type} onValueChange={(value) => setFormData({...formData, partnership_type: value})}>
                  <SelectTrigger data-testid="partner-type-select" className="rounded-none border-slate-300"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="travel_agent">Travel Agent</SelectItem>
                    <SelectItem value="corporate">Corporate Partner</SelectItem>
                    <SelectItem value="tourism_board">Tourism Board</SelectItem>
                    <SelectItem value="technology">Technology Partner</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Message</label>
                <Textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} data-testid="partner-message-input" rows={5} placeholder="Tell us about your partnership interest..." className="rounded-none border-slate-300" />
              </div>
              <Button type="submit" data-testid="partner-submit-btn" disabled={loading} className="w-full bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] py-6 rounded-none font-semibold">
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
